import { Box, Group, Stack, TextInput, Textarea, Tooltip } from '@mantine/core';
import { PredefinedDataTriplet } from 'components/inputs/predefined-data-custom-input/PredefinedDataCustomInput';
import { IconTrashX } from '@tabler/icons-react';
import ActionIcon from 'components/ActionIcon';
import { noop } from 'lodash';
import { useCallback, useMemo } from 'react';
import P3Regular from 'components/typography/P3Regular';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { AppButtonSize } from 'components/Button';
import RevisionPredefinedDataPicker from 'components/inputs/revision-predefined-data/RevisionPredefinedDataPicker';
import { RevisionPredefinedDataOption } from 'components/inputs/revision-predefined-data/types';

interface PredefinedDataCustomInputRowProps {
  value: PredefinedDataTriplet;
  options: RevisionPredefinedDataOption[];
  optionExists: boolean;
  reservedNames?: string[];
  deviceTypeName: string;
  predefinedDataTypeId: number;
  predefinedDataTypeName: string;
  withMoreInfo?: boolean;
  moreInfoMultiline?: boolean;
  autoFocus?: boolean;
  loading?: boolean;
  onCatalogOptionCreate?: (
    option: Pick<RevisionPredefinedDataOption, 'value' | 'label' | 'description' | 'origin'>
  ) => void;
  size?: AppButtonSize;
  onChange?: (value: PredefinedDataTriplet) => void;
  onRowDelete?: () => void;
}

/**
 * Single row in the PredefinedDataCustomInput.
 */
export default function PredefinedDataCustomInputRow({
  value,
  options,
  optionExists,
  reservedNames = [],
  deviceTypeName,
  predefinedDataTypeId,
  predefinedDataTypeName,
  withMoreInfo = false,
  moreInfoMultiline = false,
  autoFocus = false,
  loading = false,
  onCatalogOptionCreate = noop,
  size = 'lg',
  onChange = noop,
  onRowDelete = noop,
}: PredefinedDataCustomInputRowProps) {
  const { confirm } = useConfirm();
  const isOneTimeValue = value.source === 'one-time';
  const showMissingCatalogWarning = !optionExists && !isOneTimeValue;

  const questionMarkText = useMemo(
    () => `Predvolená hodnota "${value.name}" bola odstránená alebo upravená`,
    [value.name]
  );

  const confirmRowDelete = useCallback(() => {
    if (value.name === '') {
      onRowDelete();
    } else {
      confirm({
        title: 'Naozaj chcete odstrániť tento riadok?',
        content: 'Táto akcia je nevratná.',
        onConfirm: onRowDelete,
      });
    }
  }, [value, confirm, onRowDelete]);

  const moreInfoInput = useMemo(() => {
    if (!withMoreInfo) {
      return <></>;
    }

    /** Implements the on change callback for both textarea and input */
    const onChangeImpl = (e: { currentTarget: { value: string } }) =>
      onChange({ ...value, moreInfo: e.currentTarget.value });

    const placeholder = 'Zadajte vlastný popis (nepovinné)';
    const readOnly = showMissingCatalogWarning;
    const val = value.moreInfo;

    if (moreInfoMultiline) {
      return (
        <Box ml={40}>
          <Textarea
            size={size}
            value={val}
            onChange={onChangeImpl}
            placeholder={placeholder}
            readOnly={readOnly}
            autosize
          />
        </Box>
      );
    }

    return <TextInput size={size} value={val} onChange={onChangeImpl} placeholder={placeholder} readOnly={readOnly} />;
  }, [withMoreInfo, moreInfoMultiline, onChange, showMissingCatalogWarning, size, value]);

  const gridStylesDef = useMemo(() => {
    if (moreInfoMultiline) {
      return { gridTemplateColumns: '1fr', gap: '5px' };
    }

    return { gridTemplateColumns: `1fr ${withMoreInfo ? '1fr' : ''} auto`, gap: '16px', alignItems: 'flex-start' };
  }, [withMoreInfo, moreInfoMultiline]);

  const helpAndRowDelete = useMemo(
    () => (
      <Group spacing={8} w={80}>
        <Box w={16}>
          {showMissingCatalogWarning && <QuestionMarkTooltip multiline width={220} label={questionMarkText} />}
        </Box>
        <Tooltip label="Odstrániť riadok" withArrow={false} openDelay={200} offset={4}>
          <ActionIcon size={size} variant="danger-secondary" onClick={confirmRowDelete}>
            <IconTrashX />
          </ActionIcon>
        </Tooltip>
      </Group>
    ),
    [confirmRowDelete, questionMarkText, showMissingCatalogWarning, size]
  );

  return (
    <Box display="grid" sx={gridStylesDef}>
      <Box sx={moreInfoMultiline ? { display: 'grid', gridTemplateColumns: '1fr 80px', gap: '16px' } : {}}>
        <Stack spacing={4}>
          {showMissingCatalogWarning ? (
            <TextInput value={value.name} size={size} readOnly />
          ) : (
            <RevisionPredefinedDataPicker
              options={options}
              value={value}
              reservedNames={reservedNames}
              size={size}
              searchable
              autoFocus={autoFocus}
              loading={loading}
              hideDescription
              deviceTypeName={deviceTypeName}
              predefinedDataTypeId={predefinedDataTypeId}
              predefinedDataTypeName={predefinedDataTypeName}
              onCatalogOptionCreate={onCatalogOptionCreate}
              onChange={(nextValue) =>
                onChange({
                  ...value,
                  name: nextValue?.name ?? '',
                  description: nextValue?.description ?? '',
                  source: nextValue?.source,
                })
              }
            />
          )}
          {value.description !== '' && (
            <P3Regular pl={24} pr={16} color="gray.6" sx={{ whiteSpace: 'pre-wrap' }}>
              {value.description}
            </P3Regular>
          )}
        </Stack>
        {moreInfoMultiline && helpAndRowDelete}
      </Box>
      {moreInfoInput}
      {!moreInfoMultiline && helpAndRowDelete}
    </Box>
  );
}
